import {
  Card,
  CardContent,
  CardHeader,
  CardActions,
  Typography,
  Checkbox,
  Select,
  MenuItem,
  Button,
  Grid,
  Stack,
  TextField,
  Tabs,
  Tab,
  FormControlLabel,
  SelectChangeEvent,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import { useAppSelector, useAppDispatch } from '../../../redux-store/hooks'
import { ChangeEvent, useState } from 'react'
import { DISCOUNT_TYPE, DiscountComponent } from '../../../all-types'
import {
  addDiscountComponent,
  deactivateDiscountComponent,
  deleteDiscountComponent,
  updateDiscountComponent,
} from '../../../redux-store'
import uuid from 'react-uuid'
import {
  AddCircleOutline,
  Edit,
  Delete,
  Block,
  Percent,
  LocalOffer,
} from '@mui/icons-material'
import { CommonBtn } from '../../../components'

const defaultDiscountOptions: DiscountComponent = {
  discountId: '',
  discountName: '',
  applicableDiscount: 0,
  discountType: DISCOUNT_TYPE.PERCENTAGE,
  isActive: false,
  startDate: '',
  endDate: '',
}

const DiscountCard = ({
  discount,
  deleteDiscount,
  editDiscount,
  deactivateDiscount,
}: {
  discount: DiscountComponent
  deleteDiscount: () => void
  editDiscount: () => void
  deactivateDiscount: () => void
}) => (
  <Card sx={{ borderRadius: 2, boxShadow: 3 , minWidth: 300}}>
    <CardHeader
      avatar={<LocalOffer color="primary" />}
      title={discount.discountName}
      subheader={`ID: ${discount.discountId}`}
      sx={{ pb: 0 }}
    />
    <CardContent>
      <Stack direction="row" spacing={1} alignItems="center" mb={1}>
        <Typography variant="body2">Discount:</Typography>
        <Chip
          icon={<Percent />}
          label={`${discount.applicableDiscount} ${discount.discountType}`}
          color="success"
          variant="outlined"
          size="small"
        />
      </Stack>
      <Typography variant="body2">
        Active: {discount.isActive ? '✅ Yes' : '❌ No'}
      </Typography>
      <Typography variant="body2">Start: {discount.startDate}</Typography>
      <Typography variant="body2">End: {discount.endDate || '—'}</Typography>
    </CardContent>
    <CardActions sx={{ justifyContent: 'flex-end' }}>
      <IconButton size="small" color="primary" onClick={() => editDiscount()}>
        <Edit />
      </IconButton>
      <IconButton size="small" color="error" onClick={() => deleteDiscount()}>
        <Delete />
      </IconButton>
      <IconButton
        size="small"
        color="warning"
        onClick={() => deactivateDiscount()}
      >
        <Block />
      </IconButton>
    </CardActions>
  </Card>
)

const ClientDiscountList = () => {
  const activeDiscounts = useAppSelector(
    (state) => state.clientInfo.data.discountManager?.activeDiscounts
  )
  const deactivatedDiscounts = useAppSelector(
    (state) => state.clientInfo.data.discountManager?.deactiveDiscounts
  )
  const dispatcher = useAppDispatch()

  const [tab, setTab] = useState(0)
  const [open, setOpen] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [newDiscount, setNewDiscount] = useState<DiscountComponent>(
    defaultDiscountOptions
  )

  const handleInputChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target
    setNewDiscount({ ...newDiscount, [name]: value })
  }

  const handleSelectChange = (e: SelectChangeEvent<DISCOUNT_TYPE>) => {
    setNewDiscount({
      ...newDiscount,
      discountType: e.target.value as DISCOUNT_TYPE,
    })
  }

  const handleCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
    setNewDiscount({ ...newDiscount, [e.target.name]: e.target.checked })
  }

  const addDiscount = async () => {
    if (isEditing) {
      await dispatcher(updateDiscountComponent(newDiscount))
      setIsEditing(false)
    } else {
      await dispatcher(
        addDiscountComponent({
          ...newDiscount,
          discountId: uuid(),
          startDate: String(new Date().getTime()),
        })
      )
    }
    setNewDiscount(defaultDiscountOptions)
    setOpen(false)
  }

  const list = tab === 0 ? activeDiscounts : deactivatedDiscounts

  return (
    <div>
      <Stack alignItems="flex-end" m={2}>
        <CommonBtn
          variant="contained"
          startIcon={<AddCircleOutline />}
          onClick={() => setOpen(true)}
        >
          Add Discount
        </CommonBtn>
      </Stack>

      <Tabs value={tab} onChange={(e, val) => setTab(val)} sx={{ mb: 2 }}>
        <Tab label="Active Discounts" />
        <Tab label="Deactivated Discounts" />
      </Tabs>

      <Grid container spacing={2}>
        {Object.values(list || {}).map((discount) => (
          <Grid item xs={12} sm={6} md={4} key={discount.discountId}>
            <DiscountCard
              discount={discount}
              deleteDiscount={() =>
                dispatcher(deleteDiscountComponent(discount))
              }
              editDiscount={() => {
                setIsEditing(true)
                setNewDiscount(discount)
                setOpen(true)
                //dispatcher(updateDiscountComponent(discount))
              }}
              deactivateDiscount={() =>
                dispatcher(deactivateDiscountComponent(discount))
              }
            />
          </Grid>
        ))}
      </Grid>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add New Discount</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              name="discountName"
              value={newDiscount.discountName}
              onChange={handleInputChange}
              label="Discount Name"
              fullWidth
            />
            <TextField
              name="applicableDiscount"
              value={newDiscount.applicableDiscount}
              onChange={handleInputChange}
              label="Applicable Discount"
              fullWidth
            />
            <Select
              value={newDiscount.discountType}
              onChange={handleSelectChange}
              fullWidth
            >
              <MenuItem value={DISCOUNT_TYPE.PERCENTAGE}>Percentage</MenuItem>
              <MenuItem value={DISCOUNT_TYPE.CASH}>Cash</MenuItem>
            </Select>
            <FormControlLabel
              control={
                <Checkbox
                  name="isActive"
                  checked={newDiscount.isActive}
                  onChange={handleCheckboxChange}
                />
              }
              label="Active"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<AddCircleOutline />}
            onClick={addDiscount}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export { ClientDiscountList }
